import { useState, useEffect } from 'react'
import { useSearchParams } from 'next/navigation'

interface UsePaginationProps {
  defaultPage?: number
  defaultItemsPerPage?: number
  totalItems: number
}

export function usePagination({
  defaultPage = 1,
  defaultItemsPerPage = 25,
  totalItems
}: UsePaginationProps) {
  const searchParams = useSearchParams()
  
  // Get initial values from URL if available
  const urlPageSize = searchParams.get("pageSize")
  const urlPage = searchParams.get("page")
  
  const [currentPage, setCurrentPage] = useState(urlPage ? parseInt(urlPage) : defaultPage)
  const [itemsPerPage, setItemsPerPage] = useState(urlPageSize ? parseInt(urlPageSize) : defaultItemsPerPage)
  
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage))
  
  // Handle page change
  const goToPage = (page: number) => {
    // Make sure page is within valid range
    const validPage = Math.max(1, Math.min(page, totalPages))
    
    // Update URL with new page parameter
    const params = new URLSearchParams(searchParams.toString())
    params.set("page", validPage.toString())
    
    // Update URL without refreshing the page
    if (typeof window !== 'undefined') {
      window.history.pushState({}, '', `?${params.toString()}`)
    }
    
    setCurrentPage(validPage)
  }
  
  // Handle items per page change
  const changeItemsPerPage = (count: number) => {
    // Update URL with new pageSize parameter
    const params = new URLSearchParams(searchParams.toString())
    params.set("pageSize", count.toString())
    params.set("page", "1") // Reset to first page when changing items per page
    
    // Update URL without refreshing the page
    if (typeof window !== 'undefined') {
      window.history.pushState({}, '', `?${params.toString()}`)
    }
    
    setItemsPerPage(count)
    setCurrentPage(1) // Reset to first page
  }
  
  // Paginate data
  const paginate = <T>(data: T[]): T[] => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return data.slice(startIndex, endIndex)
  }
  
  // Ensure current page is valid when total items or items per page changes
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      goToPage(totalPages)
    }
  }, [totalItems, itemsPerPage])
  
  return {
    currentPage,
    itemsPerPage,
    totalPages,
    goToPage,
    changeItemsPerPage,
    paginate
  }
}
